import React, { useState, useMemo, useEffect, useRef } from 'react';
import { TrashIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { Block, BlockContent, DecompositionMode } from '../../types';
import { Ouvrage } from '../../../1-bibliotheque-prix/data/ServicesData';
import {
  BlockCalculationService,
  DecompositionCalculationService,
  DecompositionParams,
} from '../../services';
import { parsePrixString } from '../../../6-challenger-devis/utils/priceParsing';
import { computeBenchmarkStats } from '../../../6-challenger-devis/utils/benchmarkStats';
import {
  getPrixOriginState,
  getPrixOriginColor,
  RELIABILITY_DOT,
  sourcesToReliability,
  reliabilityTextColor,
  FPLabel,
} from '../atoms';
import { fuzzyMatchOuvrages } from '../../../6-challenger-devis/utils/fuzzyMatch';
import PuSourcesModal from '../molecules/PuSourcesModal';
import DecompositionInlineSubbar from '../molecules/DecompositionInlineSubbar';

interface VentilationCostRowProps {
  block: Block;
  parentBlock: Block;
  allBlocks: Block[];
  ouvrages: Ouvrage[];
  onBlocksChange: (blocks: Block[]) => void;
  /** Paramètres MO/appro — activent la décomposition F/MO sur la ligne. */
  decompositionParams?: DecompositionParams;
}

const VentilationCostRow: React.FC<VentilationCostRowProps> = ({
  block,
  parentBlock,
  allBlocks,
  ouvrages,
  onBlocksChange,
  decompositionParams,
}) => {
  const [intitule, setIntitule] = useState(block.content.intitule ?? '');
  const [quantite, setQuantite] = useState(block.content.quantite ?? '1');
  const [unite, setUnite] = useState(block.content.unite ?? '');
  const [prixUnitaire, setPrixUnitaire] = useState(
    parsePrixString(block.content.prixUnitaire ?? '0').toString()
  );
  const [showPuModal, setShowPuModal] = useState(false);

  // Mode de décomposition de la ligne ventilée (orthogonal à la ventilation du parent).
  const decompositionMode: DecompositionMode = block.content.decompositionMode ?? 'forfaitaire';
  const isDetaillee = decompositionMode !== 'forfaitaire';

  // Sync state when the block identity changes (external update)
  useEffect(() => {
    setIntitule(block.content.intitule ?? '');
    setQuantite(block.content.quantite ?? '1');
    setUnite(block.content.unite ?? '');
    setPrixUnitaire(parsePrixString(block.content.prixUnitaire ?? '0').toString());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  // Ref to track latest intitule value for the onBlur setTimeout closure
  const latestIntituleRef = useRef(intitule);
  useEffect(() => { latestIntituleRef.current = intitule; }, [intitule]);

  // Prevents the onBlur setTimeout from overwriting a freshly committed ouvrage selection
  const skipNextBlurCommitRef = useRef(false);

  // Autocomplete state
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Ligne ventilée → recherche limitée aux ouvrages F+P (prestation vendue
  // fourni + posé). Les fournitures nues (F) ne sont pas pertinentes ici :
  // une ligne ventilée représente une prestation du chiffrage, pas un
  // composant matière.
  const emptySuggestions = useMemo(() => {
    const label = parentBlock.content.intitule || parentBlock.content.titre || '';
    if (!label.trim()) return [];
    return fuzzyMatchOuvrages(label, ouvrages, 'F+P')
      .filter(r => r.score > 0.2)
      .slice(0, 6)
      .map(r => r.ouvrage);
  }, [parentBlock, ouvrages]);

  // Résultats selon la saisie : suggestions si vide, recherche sinon
  const searchResults = useMemo(() => {
    if (intitule.length < 2) return emptySuggestions;
    const query = intitule.toLowerCase();
    return ouvrages
      .filter(o => o.type === 'F+P')
      .filter(o =>
        o.intitule.toLowerCase().includes(query) ||
        o.categorie.toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [intitule, ouvrages, emptySuggestions]);

  const computedTotal = (() => {
    // En mode détaillé, le total persisté par la décomposition (inclut l'appro
    // forfaitaire en fourni-pose) fait foi. Sinon on recalcule localement à
    // partir du state (saisies non encore commitées).
    if (isDetaillee) {
      return block.content.prixTotal ?? BlockCalculationService.formatPrice(0);
    }
    const qty = parseFloat(quantite.replace(',', '.')) || 0;
    const pu = parsePrixString(prixUnitaire);
    return BlockCalculationService.formatPrice(qty * pu);
  })();

  const commitChanges = (overrides?: Partial<{
    intitule: string; quantite: string; unite: string; prixUnitaire: string;
    prixUnitaireSource?: string; prixUnitaireMatchType?: string;
    prixUnitaireReliability?: import('../../types').PrixReliability;
    prixUnitaireSources?: import('../../types').PrixUnitaireSourceItem[];
  }>) => {
    const merged = {
      intitule: overrides?.intitule ?? intitule,
      quantite: overrides?.quantite ?? quantite,
      unite: overrides?.unite ?? unite,
      prixUnitaire: overrides?.prixUnitaire ?? prixUnitaire,
    };

    // Traçabilité de l'origine du prix
    const sourceOverrides = overrides?.prixUnitaireSource !== undefined
      ? {
          prixUnitaireSource: overrides.prixUnitaireSource as 'database' | 'user',
          prixUnitaireMatchType: overrides.prixUnitaireMatchType as 'prestation' | 'ensemble' | undefined,
          ...(overrides.prixUnitaireReliability !== undefined
            ? { prixUnitaireReliability: overrides.prixUnitaireReliability }
            : {}),
          ...(overrides.prixUnitaireSources !== undefined
            ? { prixUnitaireSources: overrides.prixUnitaireSources }
            : {}),
        }
      : (() => {
          // Si l'utilisateur a changé le prix d'un bloc dont la source était 'database'
          const originalPU = parsePrixString(block.content.prixUnitaire ?? '0');
          const newPU = parsePrixString(merged.prixUnitaire);
          if (newPU !== originalPU && block.content.prixUnitaireSource === 'database') {
            return {
              prixUnitaireOriginal: block.content.prixUnitaire,
              prixUnitaireSource: 'user' as const,
              // Conserver le matchType pour que le badge passe en 'modified'
              prixUnitaireMatchType: block.content.prixUnitaireMatchType,
            };
          }
          return {};
        })();

    // Content de base après merge des champs de ligne (intitulé / qté / unité).
    const baseContent: BlockContent = {
      ...block.content,
      ...sourceOverrides,
      titre: merged.intitule,
      intitule: merged.intitule,
      quantite: merged.quantite,
      unite: merged.unite,
    };

    let nextContent: BlockContent;
    if (isDetaillee && decompositionParams) {
      // Ligne ventilée en mode décomposé : PU et prixTotal sont DÉRIVÉS
      // de la décomposition F/MO (plus aucune saisie directe du PU).
      nextContent = DecompositionCalculationService.applyBreakdownToContent(
        baseContent, merged.quantite, decompositionParams,
      );
    } else {
      // Mode forfaitaire : PU saisi librement, total = PU × qté.
      const qty = parseFloat(merged.quantite.replace(',', '.')) || 0;
      const pu = parsePrixString(merged.prixUnitaire);
      const total = BlockCalculationService.formatPrice(qty * pu);
      nextContent = {
        ...baseContent,
        prixUnitaire: `${merged.prixUnitaire} €`,
        prixTotal: total,
      };
    }

    const updated = allBlocks.map(b =>
      b.id === block.id ? { ...b, content: nextContent } : b
    );
    onBlocksChange(updated);
  };

  // Handler passé à DecompositionInlineSubbar. Applique la décomposition pour
  // dériver PU + prixTotal, synchronise le state local du PU, puis persiste.
  const handleDecompContentChange = (nextContent: BlockContent) => {
    const applied = decompositionParams && DecompositionCalculationService.isDetailleeContent(nextContent)
      ? DecompositionCalculationService.applyBreakdownToContent(
          nextContent, nextContent.quantite, decompositionParams,
        )
      : nextContent;

    // Sync state local du PU (l'input est readOnly en mode détaillé mais on
    // met à jour la valeur affichée côté composant pour qu'elle reste cohérente
    // avec ce qui est persisté).
    if (applied.prixUnitaire) {
      setPrixUnitaire(parsePrixString(applied.prixUnitaire).toString());
    }

    const updated = allBlocks.map(b =>
      b.id === block.id ? { ...b, content: applied } : b
    );
    onBlocksChange(updated);
  };

  const handleSelectOuvrage = (ouvrage: Ouvrage) => {
    const stats = computeBenchmarkStats(ouvrage);
    const avgStr = stats.avg > 0 ? BlockCalculationService.formatPrice(stats.avg) : '0';

    // Prevent the onBlur setTimeout from overwriting this commit
    skipNextBlurCommitRef.current = true;

    setIntitule(ouvrage.intitule);
    setUnite(ouvrage.unite);
    setPrixUnitaire(avgStr);
    setIsOpen(false);
    setSelectedIndex(-1);

    // Map ouvrage.chantiers → PrixUnitaireSourceItem[] pour alimenter la
    // pastille de fiabilité + la modale de sélection des sources (clic droit).
    // Même logique que VentilationSourceRow.buildCostLinesForResult.
    const dbSources = ouvrage.chantiers
      .map(c => ({
        id: c.id,
        nom: c.nom,
        localisation: c.localisation,
        annee: c.annee,
        fournisseur: c.fournisseur,
        prixUnitaire: parsePrixString(c.prixActualise || c.prixUnitaire),
        active: true,
      }))
      .filter(s => s.prixUnitaire > 0);

    commitChanges({
      intitule: ouvrage.intitule,
      unite: ouvrage.unite,
      prixUnitaire: avgStr,
      prixUnitaireSource: 'database',
      prixUnitaireMatchType: 'prestation',
      prixUnitaireReliability: sourcesToReliability(dbSources.length),
      prixUnitaireSources: dbSources,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && selectedIndex >= 0 && selectedIndex < searchResults.length) {
        handleSelectOuvrage(searchResults[selectedIndex]);
      } else {
        // Pas de sélection dans le dropdown → fermer et valider la saisie libre
        setIsOpen(false);
        e.currentTarget.blur();
      }
      return;
    }

    if (!isOpen || searchResults.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < searchResults.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : searchResults.length - 1);
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleDelete = () => {
    const siblingCostLines = allBlocks.filter(
      b => b.parentId === parentBlock.id && b.content.isVentilationCostLine && b.id !== block.id
    );

    let updatedBlocks = allBlocks.filter(b => b.id !== block.id);

    if (siblingCostLines.length === 0) {
      updatedBlocks = updatedBlocks.map(b =>
        b.id === parentBlock.id
          ? { ...b, content: { ...b.content, matchedItemId: undefined, matchedItemType: undefined } }
          : b
      );
    }

    onBlocksChange(updatedBlocks);
  };

  // Couleur du prix selon l'origine (dérivé du contenu du bloc persisté)
  const originColor = getPrixOriginColor(getPrixOriginState(
    block.content.prixUnitaireSource,
    block.content.prixUnitaireMatchType,
  ));

  return (
    <>
    <div className="group flex items-center gap-2 pr-6 py-2 bg-violet-50 border-b border-violet-200 last:border-b-0">
      {/* Connecteur arbre */}
      <span className="w-10 flex-shrink-0 flex items-center justify-end pr-1 text-violet-300 text-base leading-none select-none">└</span>

      {/* Intitulé — même style que ChiffMenuSearchBar */}
      <div className="relative flex-1 min-w-0">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          value={intitule}
          onChange={e => {
            setIntitule(e.target.value);
            setIsOpen(true);
            setSelectedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => {
            setIsOpen(false);
            if (!skipNextBlurCommitRef.current) {
              commitChanges({ intitule: latestIntituleRef.current });
            }
            skipNextBlurCommitRef.current = false;
          }, 300)}
          placeholder="Rechercher une prestation…"
          className="w-full pl-9 pr-3 py-1 text-sm border border-gray-200 rounded leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
        />

        {/* Dropdown résultats */}
        {isOpen && searchResults.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {searchResults.map((ouvrage, index) => (
              <div
                key={ouvrage.id}
                className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                  index === selectedIndex
                    ? 'bg-violet-600 text-white'
                    : 'text-gray-900 hover:bg-gray-100'
                }`}
                onClick={() => handleSelectOuvrage(ouvrage)}
              >
                <div className="flex items-center gap-2">
                  <FPLabel ouvrageType={ouvrage.type} />
                  <span className="font-medium truncate">{ouvrage.intitule}</span>
                </div>
                <div className={`text-xs mt-0.5 truncate ${
                  index === selectedIndex ? 'text-violet-200' : 'text-gray-500'
                }`}>
                  {ouvrage.categorie}{ouvrage.unite ? ` • ${ouvrage.unite}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}

        {isOpen && intitule.length >= 2 && searchResults.length === 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white shadow-lg rounded-md py-2 px-3 text-sm text-gray-500 ring-1 ring-black ring-opacity-5">
            Aucun résultat pour « {intitule} »
          </div>
        )}

        {/* État vide : message si pas de suggestions disponibles */}
        {isOpen && intitule.length < 2 && searchResults.length === 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white shadow-lg rounded-md py-2 px-3 text-sm text-gray-400 ring-1 ring-black ring-opacity-5 italic">
            Tapez pour rechercher une prestation…
          </div>
        )}
      </div>

      {/* Label discret F+P / F */}
      <FPLabel mode={decompositionMode} />

      {/* Quantité */}
      <input
        type="text"
        value={quantite}
        onChange={e => setQuantite(e.target.value)}
        onBlur={e => commitChanges({ quantite: e.target.value })}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        placeholder="Qté"
        className="w-16 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white text-right"
      />

      {/* Unité */}
      <input
        type="text"
        value={unite}
        onChange={e => setUnite(e.target.value)}
        onBlur={e => commitChanges({ unite: e.target.value })}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        placeholder="U"
        className="w-14 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white text-center"
      />

      {/* Prix unitaire — structure unifiée : même layout (pastille + input + €)
          que la ligne soit matchée DB ou vide. Quand DB (reliability + sources),
          pastille colorée + tooltip (min/max/avg/médiane) + clic droit pour la
          modale de sélection. Sinon, pastille grise discrète et input simple.
          L'uniformité garantit l'alignement pixel-perfect entre états. */}
      {(() => {
        const hasDBData = block.content.prixUnitaireReliability
          && block.content.prixUnitaireSource === 'database'
          && (block.content.prixUnitaireSources?.length ?? 0) > 0;
        const rel = block.content.prixUnitaireReliability;
        const cfg = rel ? RELIABILITY_DOT[rel] : null;
        const dotClass = hasDBData && cfg ? cfg.dot : 'bg-gray-300';
        const textColor = rel ? reliabilityTextColor(rel) : '';

        let statsEl: React.ReactNode = null;
        if (hasDBData) {
          const activeSources = block.content.prixUnitaireSources?.filter(s => s.active) ?? [];
          if (activeSources.length > 0) {
            const prices = activeSources.map(s => s.prixUnitaire).sort((a, b) => a - b);
            const sum = prices.reduce((a, b) => a + b, 0);
            const avg = sum / prices.length;
            const mid = Math.floor(prices.length / 2);
            const median = prices.length % 2 === 0 ? (prices[mid - 1] + prices[mid]) / 2 : prices[mid];
            const fmt = (n: number) => BlockCalculationService.formatPrice(n);
            const uniteStr = block.content.unite ?? '';
            statsEl = (
              <>
                <span className="space-y-1 block text-gray-300 mt-1.5">
                  <span className="flex justify-between"><span>Min</span><span className="tabular-nums">{fmt(prices[0])} €{uniteStr && `/${uniteStr}`}</span></span>
                  <span className="flex justify-between"><span>Max</span><span className="tabular-nums">{fmt(prices[prices.length - 1])} €{uniteStr && `/${uniteStr}`}</span></span>
                  <span className="flex justify-between font-semibold text-white"><span>Moy.</span><span className="tabular-nums">{fmt(avg)} €{uniteStr && `/${uniteStr}`}</span></span>
                  <span className="flex justify-between"><span>Médiane</span><span className="tabular-nums">{fmt(median)} €{uniteStr && `/${uniteStr}`}</span></span>
                </span>
                <span className="flex justify-between items-center mt-1.5 pt-1.5 border-t border-gray-700 text-gray-400 text-[10px]">
                  <span>{activeSources.length} référence{activeSources.length > 1 ? 's' : ''}</span>
                  <span className="italic">Clic droit → détails</span>
                </span>
              </>
            );
          }
        }

        return (
          <div
            className="relative group/pu flex items-center gap-1 flex-shrink-0"
            onContextMenu={hasDBData ? (e) => {
              if (isDetaillee) return;
              e.preventDefault();
              e.stopPropagation();
              setShowPuModal(true);
            } : undefined}
          >
            <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`} />
            <input
              type="text"
              value={prixUnitaire}
              onChange={e => { if (!isDetaillee) setPrixUnitaire(e.target.value); }}
              onBlur={e => { if (!isDetaillee) commitChanges({ prixUnitaire: e.target.value }); }}
              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              placeholder="PU"
              readOnly={isDetaillee}
              title={isDetaillee
                ? 'PU calculé par la décomposition F/MO — éditez les champs du panneau ambre ci-dessous'
                : hasDBData ? 'Clic droit → modifier la sélection des sources' : undefined}
              className={`w-20 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white text-right font-medium ${originColor} ${isDetaillee ? 'opacity-70 cursor-not-allowed' : ''}`}
            />
            <span className="text-xs text-gray-400">€</span>
            {hasDBData && cfg && (
              <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-52 opacity-0 group-hover/pu:opacity-100 transition-opacity duration-150 z-50">
                <span className="block bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl">
                  <span className={`flex items-center gap-1 mb-1 ${textColor} font-semibold`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                    {cfg.label}
                  </span>
                  <span className="text-gray-400 text-[10px] block mb-1.5">{cfg.reason}</span>
                  <span className="font-medium text-gray-100 block mb-0.5 truncate">
                    {block.content.intitule || block.content.titre}
                  </span>
                  {statsEl}
                </span>
                <span className="absolute top-full right-3 border-4 border-transparent border-t-gray-900" />
              </span>
            )}
          </div>
        );
      })()}

      {/* Total calculé (readonly) */}
      <div className="w-20 text-right text-sm font-medium text-gray-700 flex-shrink-0">
        {computedTotal} €
      </div>

      {/* Bouton décomposition — même design que les lignes de prestation
          (BlockRow). Click bascule en fourni-pose et affiche la subbar ;
          pastille violette quand actif. Fermeture via × de la subbar. */}
      {decompositionParams && (
        <div className="w-8 flex-shrink-0 flex items-center justify-center">
          <button
            type="button"
            onClick={() => {
              const nextMode: DecompositionMode = isDetaillee ? 'forfaitaire' : 'fourni-pose';
              const updated = allBlocks.map(b =>
                b.id === block.id
                  ? { ...b, content: { ...b.content, decompositionMode: nextMode } }
                  : b,
              );
              onBlocksChange(updated);
            }}
            className="relative w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
            title={isDetaillee ? 'Masquer la décomposition' : 'Afficher la décomposition F / MO / Appro'}
          >
            {isDetaillee && (
              <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-violet-500" />
            )}
            <Squares2X2Icon className={`w-3.5 h-3.5 transition-colors ${isDetaillee ? 'text-violet-600' : 'text-gray-300 group-hover:text-gray-400'}`} />
          </button>
        </div>
      )}

      {/* Bouton supprimer */}
      <button
        onClick={handleDelete}
        title="Supprimer cette ligne"
        className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-red-400 hover:bg-red-50 transition-colors"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </div>

    {/* Décomposition F/MO de la ligne ventilée — bandeau inline (piste 2 /
        modèle A) : visible uniquement quand mode !== 'forfaitaire'. Pas
        d'accordéon. La fermeture (×) repasse en forfaitaire (valeurs F/MO
        conservées pour une réactivation rapide).
        Mutex avec la ventilation parentale : la décomposition vit au niveau
        de chaque ligne ventilée, jamais au niveau du parent ventilé. */}
    {decompositionParams && isDetaillee && (
      <DecompositionInlineSubbar
        block={block}
        params={decompositionParams}
        onContentChange={handleDecompContentChange}
        onClose={() => {
          const updated = allBlocks.map(b =>
            b.id === block.id
              ? { ...b, content: { ...b.content, decompositionMode: 'forfaitaire' as DecompositionMode } }
              : b,
          );
          onBlocksChange(updated);
        }}
      />
    )}

    {showPuModal && (
      <PuSourcesModal
        block={block}
        onClose={() => setShowPuModal(false)}
        onUpdate={(updates) => {
          const nextContent = updates.content ?? block.content;
          const updated = allBlocks.map(b =>
            b.id === block.id ? { ...b, ...updates, content: nextContent } : b
          );
          onBlocksChange(updated);
          setPrixUnitaire(parsePrixString(nextContent.prixUnitaire ?? '0').toString());
        }}
      />
    )}
    </>
  );
};

export default VentilationCostRow;
